/**
 * TrackerModelsPage - Modelos de rastreador (fabricante + modelo)
 *
 * RELEASE: 2026-10-09 v1.2.0 - Filtros rápidos no estilo pill padrão (QUICK_FILTER_PILL) e busca com lupa.
 * RELEASE: 2026-10-06 v1.1.0 - Exibe tecnologias de conectividade configuradas.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 2 do Estoque).
 */
import { useEffect, useState } from 'react';
import { Cpu, MoreHorizontal, Pencil, Plus, Power, Search } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageLayout, ListPageHeader, ListPageFiltersBar, ListPageContent,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { trackerConnectivityLabel, useStockRegistryMutations, useTrackerModels, type StockListFilters, type TrackerModel } from '@/hooks/useStockRegistries';
import { TrackerModelFormDialog } from '@/components/stock/TrackerModelFormDialog';

export default function TrackerModelsPage() {
  const { canCreate, canEdit, isAdmin } = usePermissionsContext();
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<StockListFilters>({ search: '', status: 'active', page: 1, pageSize: 25 });
  const [editor, setEditor] = useState<{ open: boolean; item: TrackerModel | null }>({ open: false, item: null });
  const { data, isLoading } = useTrackerModels(filters);
  const { upsertModel } = useStockRegistryMutations();

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const allowCreate = isAdmin || canCreate('stock_models');
  const allowEdit = isAdmin || canEdit('stock_models');
  const items = data?.items ?? [];

  const toggleActive = (m: TrackerModel) =>
    upsertModel.mutate({ id: m.id, data: { name: m.name, notes: m.notes, manufacturer_person_id: m.manufacturer_person_id, connectivity_technologies: m.connectivity_technologies, is_active: !m.is_active } });

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Modelos de rastreador"
        actions={allowCreate ? (
          <Button size="xs" onClick={() => setEditor({ open: true, item: null })}>
            <Plus className="mr-1.5 h-4 w-4" /> Novo modelo
          </Button>
        ) : undefined}
      />
      <ListPageFiltersBar>
        {/* Busca com lupa, mesmo padrão da listagem de Propostas */}
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground sm:h-3.5 sm:w-3.5" />
          <Input className={`${LIST_FILTERS_INPUT_CLASS} w-full pl-8`} value={searchInput} maxLength={100}
          placeholder="Buscar por modelo ou fabricante..." onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v as StockListFilters['status'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>
      <ListPageContent>
        {isLoading ? <Skeleton className="h-40 w-full rounded-lg" /> : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Cpu className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nenhum modelo encontrado.</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table className="min-w-[600px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Fabricante</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Conectividade</TableHead>
                  <TableHead className="w-24">Situação</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => (
                  <TableRow key={m.id} className="cursor-pointer" onClick={() => allowEdit && setEditor({ open: true, item: m })}>
                    <TableCell className="text-sm">{m.manufacturer_name ?? '—'}</TableCell>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {m.connectivity_technologies.length > 0
                          ? m.connectivity_technologies.map((technology) => <Badge key={technology} variant="secondary">{trackerConnectivityLabel(technology)}</Badge>)
                          : <span className="text-sm text-muted-foreground">—</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider">
                        <StatusDot tone={m.is_active ? 'emerald' : 'muted'} />{m.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {allowEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditor({ open: true, item: m })}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleActive(m)}><Power className="mr-2 h-4 w-4" /> {m.is_active ? 'Inativar' : 'Ativar'}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50, 100]} />
      </ListPageContent>
      <TrackerModelFormDialog open={editor.open} model={editor.item} onOpenChange={(open) => setEditor((c) => ({ ...c, open }))} />
    </ListPageLayout>
  );
}
